import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { PLATFORMS } from 'frontend-utils/utils/constants/constant-values';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  MEALTYPE_TRANSLATION_KEYS,
  PLATFORM_TRANSLATION_KEYS,
} from 'lumen/utils/constants/general-constants';
import {
  DEFAULT_POS,
  RATE_TYPE_TO_RATE_PARAM,
  RATE_TYPE_TRANSLATIONS,
} from 'lumen/utils/constants/pi-constant-values';

export default class MarketParityInsightHistoricalController extends Controller {
  queryParams = [
    {
      hotelId: { scope: 'controller' },
      exportDate: { scope: 'controller' },
      platform: { scope: 'controller' },
    },
  ];

  @service ajax;
  @service intl;
  @service router;
  @service usageAnalytics;

  // Parent controller
  @controller('market.parity.insight') parentController;

  @tracked exportDate = null;
  @tracked hotelId = null;
  @tracked platform =
    DEFAULT_QP_VALUES.market.parity.insight.historical.platform;

  get availableOTAs() {
    return this.hotel.getParityAnalyticsOTAs(this.platform);
  }

  get exportDateOptions() {
    return this.model.exports.map((exportDate) => ({
      id: exportDate.extract_date,
      label: this.intl.t('parity_analytics.shop_range', {
        formattedExtractDate: dayjs(exportDate.extract_date)
          .startOf('week')
          .format('LL'),
        formattedExtractDateEndOfWeek: dayjs(exportDate.extract_date)
          .endOf('week')
          .format('LL'),
      }),
    }));
  }

  get filterSelections() {
    const selectedExportDate = this.model.exports.find(
      (exportDate) => exportDate.extract_date === this.exportDate,
    );

    if (selectedExportDate) {
      return {
        los: selectedExportDate.los,
        persons: selectedExportDate.persons,
      };
    }

    return { los: null, persons: null };
  }

  get filterString() {
    const filters = [];

    if (this.parentController.showMemberRate) {
      filters.push(this.intl.t('parity_strategy.membership.member'));
    }

    filters.push(
      this.intl.t(RATE_TYPE_TRANSLATIONS[this.parentController.rateType]),
    );

    if (this.filterSelections.los) {
      filters.push(
        this.intl.t('rates.nights', { count: this.filterSelections.los }),
      );
    }

    if (this.filterSelections.persons) {
      filters.push(
        this.intl.t('rates.persons_selection', {
          count: this.filterSelections.persons,
        }),
      );
    }

    filters.push(
      this.intl.t(MEALTYPE_TRANSLATION_KEYS[this.parentController.mealPlan]),
    );

    if (this.parentController.pos !== DEFAULT_POS) {
      filters.push(
        `${this.intl.t('general.pos_short')}: ${this.parentController.pos}`,
      );
    }

    return filters.join(' - ');
  }

  get hasNoExportData() {
    return this.model.exports.length === 0;
  }

  get hotel() {
    return this.model?.hotel;
  }

  get hotelViewQuery() {
    const { mealPlan, pos, rateType, showMemberRate } = this.parentController;

    return {
      los: this.filterSelections.los,
      maxPersons: this.filterSelections.persons,
      platform: this.platform,
      rateParam: RATE_TYPE_TO_RATE_PARAM[rateType],
      mealPlan,
      pos,
      showMemberRate,
    };
  }

  get isLoading() {
    return (
      this.model.parities.isRunning || this.model.parityPermission.isRunning
    );
  }

  get liveParityHotelId() {
    if (this.model.parityPermission.isRunning) {
      return null;
    }

    return this.model.parityPermission.last.value.hotel_id;
  }

  get noDataForSelection() {
    if (!this.isLoading) {
      const hasParities = this.model?.parities.last.value
        .map((period) => {
          let result = 0;

          (period?.parities ?? []).forEach((parity) => {
            if (parity.message !== 'general.missing') {
              result = 1;
            }
          });
          return result;
        })
        .reduce((a, b) => a + b, 0);
      if (hasParities === 0) {
        return {
          hasMessage: true,
          message: this.intl.t('rates.nodata.refresh'),
          exportDisabled: true,
        };
      }
    }

    return {
      hasMessage: false,
      message: '',
      exportDisabled: false,
    };
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get platformOptions() {
    return this.model.hotel.parityAnalyticsPlatforms.map((platform) => ({
      id: platform,
      label: this.intl.t(PLATFORM_TRANSLATION_KEYS[platform]),
    }));
  }

  get selectedExportDate() {
    return this.exportDateOptions.find(
      (exportDate) => exportDate.id === this.exportDate,
    );
  }

  get selectedPlatformOption() {
    return this.platformOptions.find(
      (platform) => platform.id === this.platform,
    );
  }

  get showMemberRate() {
    return this.parentController.showMemberRate;
  }

  get showMeta() {
    return this.platform !== PLATFORMS.PLATFORM_MOBILE_APP;
  }

  get showTable() {
    if (this.model.parities.isRunning || this.hasNoExportData) {
      return false;
    }

    // Mobile exportConfig can be undefined (See https://otainsight.slack.com/archives/G17FSKSQ0/p1687337453451109?thread_ts=1687333112.200739&cid=G17FSKSQ0)
    return this.hotel && this.model.parities.last.value.length;
  }

  @action
  redirectOnClose() {
    this.router.legacyTransitionTo('market.parity.insight');
  }

  @action
  selectExportDate(selectedExportDate) {
    this.exportDate = selectedExportDate.id;
  }

  @action
  setPlatform(platformOption) {
    this.platform = platformOption.id;
  }

  @action
  trackRedirectToLiveView(hotelId) {
    this.usageAnalytics.track('Live View Opened', {
      eventTrigger: 'live-view-link',
      hotelId,
    });
  }
}
